import React, { useEffect } from "react";
import "./ModelPage.scss";
import RightDrawerComponent from "ui/components/drawer/RightDrawerComponent";
import FloatButtons from "ui/components/floatButton/FloatButtons";
import DialogComponent from "ui/components/dialog/DialogComponent";
import { useLocation } from "react-router-dom";
import {
    CurrentDrawerOpenType,
    hideDrawer,
    toggleDrawer,
    updateCurrentDrawerOpenType,
    updateDrawerTitle,
} from "state/slices/RightDrawerSlice";
import { useTypedDispatch, useTypedStore } from "hooks/store";
import { selectionEvents } from "Classes/Events";
import { Robot } from "Classes/Robot";
import { StaticMesh } from "Classes/StaticMesh";
import { AssetType } from "Classes/enums";
import LoadingIndicator from "ui/components/LoadingIndicator";
import {
    SelectedBSUBSUType,
    setSelectedBsu,
    setSelectedPort,
    updateSelectedConsole,
    updateSelectedStack,
    updateSelectedTrolley,
    updateSelectedSurgeon,
    updateSelectedSlave,
    updateSelectedAnesthesia,
} from "state/slices/SceneSlice";
import { builder } from "Classes/global";
import ORViewerWrapper from "ui/components/ORViewer/ORViewerWrapper";
import { Inventory } from "Classes/InventoryService";
import { isMobile } from "react-device-detect";

import shepherd from "ui/components/ORViewer/shepherd";

const ModelPage = (): JSX.Element => {
    const location = useLocation<any>();
    const sceneKey = location?.state?.sceneKey;

    if (document.body.classList.contains("newPage")) {
        shepherd();
    }

    useEffect(() => {
        // Prevent scrolling when touching the canvas
        const modelPage = document.getElementById("modelPage") as HTMLElement;
        document.body.addEventListener("touchstart", () => {
            modelPage?.classList.add("touchScroll");
        });
        document.body.addEventListener("touchend", () => {
            modelPage?.classList.remove("touchScroll");
        });
    }, []);

    const dispatch = useTypedDispatch();
    const store = useTypedStore();

    const handleEventDispatch = (type: CurrentDrawerOpenType) => {
        if (store.getState().ModelWindow.isModelOpen) {
            dispatch(updateCurrentDrawerOpenType(type));
        } else {
            dispatch(toggleDrawer(type));
        }
    };
    /**
     * Register event listener from scene.
     */
    useEffect(() => {
        selectionEvents.on("objectSelected", handleSelectionEvent);
        selectionEvents.on("clearSelection", handleClearSelection);
        selectionEvents.on("startDragging", handleStartDragging);
        return () => {
            /**
             * Unregister event listener from component.
             */
            dispatch(hideDrawer());
            selectionEvents.removeAllListeners("objectSelected");
            selectionEvents.removeAllListeners("clearSelection");
            selectionEvents.removeAllListeners("startDragging");
        };
    }, []);

    const handleStartDragging = () => {
        if (store.getState().ModelWindow.isModelOpen) {
            dispatch(toggleDrawer(store.getState().ModelWindow.type));
        }
    };

    const handleClearSelection = () => {
        if (store.getState().ModelWindow.isModelOpen) {
            dispatch(toggleDrawer(store.getState().ModelWindow.type));
        }
    };

    const handleSelectionEvent = (selectedObject: Robot | StaticMesh) => {
        switch (selectedObject.type) {
            case AssetType.Robot:
                const bsu = selectedObject as Robot;
                let type = null;
                if (bsu.name.startsWith(Inventory.iBsu.name)) {
                    type = SelectedBSUBSUType.IBsu;
                    dispatch(updateDrawerTitle(`I-BSU`));
                } else {
                    type = SelectedBSUBSUType.VBsu;
                    dispatch(updateDrawerTitle(`V-BSU`));
                }
                dispatch(
                    setSelectedBsu({
                        name: bsu.name,
                        id: bsu.lightColorName,
                        selectedHeight: parseInt(String(bsu.getArmHeight())),
                        currentRotation: bsu.getRotationY(),
                        type: type,
                    })
                );

                handleEventDispatch(CurrentDrawerOpenType.BSUObject);
                break;
            case AssetType.Console:
                dispatch(updateDrawerTitle("Surgeon Console"));
                const rotationX = (builder.room.selectedObject as StaticMesh).getRotationY();
                dispatch(updateSelectedConsole({ currentRotation: rotationX }));
                handleEventDispatch(CurrentDrawerOpenType.Console);
                break;
            case AssetType.Stack:
                dispatch(updateDrawerTitle("Stack"));
                const rotationY = (builder.room.selectedObject as StaticMesh).getRotationY();
                dispatch(updateSelectedStack({ currentRotation: rotationY }));
                handleEventDispatch(CurrentDrawerOpenType.Stack);
                break;
            case AssetType.Trolley:
                dispatch(updateDrawerTitle("Trolley"));
                const rotation = (builder.room.selectedObject as StaticMesh).getRotationY();
                dispatch(updateSelectedTrolley({ currentRotation: rotation }));
                handleEventDispatch(CurrentDrawerOpenType.Trolley);
                break;
            case AssetType.Anesthesia:
                dispatch(updateDrawerTitle("Anesthesia Machine"));
                const rotation1 = (builder.room.selectedObject as StaticMesh).getRotationY();
                dispatch(updateSelectedAnesthesia({ currentRotation: rotation1 }));
                handleEventDispatch(CurrentDrawerOpenType.Anesthesia);
                break;
            case AssetType.Surgeon:
                dispatch(updateDrawerTitle("Surgeon"));
                const rotation2 = (builder.room.selectedObject as StaticMesh).getRotationY();
                dispatch(updateSelectedSurgeon({ currentRotation: rotation2 }));
                handleEventDispatch(CurrentDrawerOpenType.Surgeon);
                break;
            case AssetType.Slave:
                dispatch(updateDrawerTitle("Slave"));
                const rotation3 = (builder.room.selectedObject as StaticMesh).getRotationY();
                dispatch(updateSelectedSlave({ currentRotation: rotation3 }));
                handleEventDispatch(CurrentDrawerOpenType.Slave);
                break;
            case AssetType.AccessPoint:
                dispatch(updateDrawerTitle("Access Point"));
                handleEventDispatch(CurrentDrawerOpenType.AccessPoint);
                break;
            case AssetType.PatientBed:
                dispatch(updateDrawerTitle("Operating Table"));
                dispatch(toggleDrawer(CurrentDrawerOpenType.Bed));
                break;
            case AssetType.Trocar:
                const mash = selectedObject as StaticMesh;
                handleEventDispatch(CurrentDrawerOpenType.Port);
                dispatch(updateDrawerTitle(`Port`));
                dispatch(
                    setSelectedPort({
                        name: selectedObject.name,
                        id: selectedObject.type.toString(),
                        color: builder.room.trocarToColor[mash.name],
                        rotateX: mash.getRotationX(),
                        rotateY: mash.getRotationY(),
                    })
                );
                // console.log("inventory", Inventory.colors);
                //builder.room.remove(selectedObject);
                break;
        }
    };

    if (isMobile) {
        return (
            <div id="modelPage" className="modelPage">
                <LoadingIndicator />
                <DialogComponent />
                <ORViewerWrapper sceneKey={sceneKey} />
                <RightDrawerComponent />
                <FloatButtons />
            </div>
        );
    }

    return (
        <div id="modelPage" className="modelPage">
            <LoadingIndicator />
            <DialogComponent />
            <ORViewerWrapper sceneKey={sceneKey} />
            <RightDrawerComponent />
            <FloatButtons />
        </div>
    );
};

export default ModelPage;
